import { useState, useEffect, useCallback } from "react";
import { useAuth } from "contexts/AuthContext";
import {
  Wifi, WifiOff, Monitor, Eye, MessageSquare, RotateCcw, Loader2,
  CheckCircle, XCircle, Clock, Trash2, Download, Play
} from "lucide-react";

const STATUS_COLORS = {
  pending: "#f59e0b",
  in_progress: "#3b82f6",
  completed: "#10b981",
  failed: "#ef4444",
};

export default function BridgeControl({ selectedEntryIds = [], onViewsUpdated }) {
  const { api } = useAuth();
  const [bridgeStatus, setBridgeStatus] = useState(null);
  const [history, setHistory] = useState([]);
  const [mappings, setMappings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [enqueueing, setEnqueueing] = useState(null);
  const [editMapping, setEditMapping] = useState(null);
  const [mappingInput, setMappingInput] = useState("");

  const fetchStatus = useCallback(async () => {
    try {
      const [statusRes, historyRes, mappingsRes] = await Promise.all([
        api.get("/bridge/status"),
        api.get("/bridge/history?limit=20"),
        api.get("/bridge/mappings"),
      ]);
      setBridgeStatus(statusRes.data);
      setHistory(historyRes.data);
      setMappings(mappingsRes.data);
    } catch (err) {
      console.error("Bridge status error:", err);
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => {
    fetchStatus();
    const interval = setInterval(fetchStatus, 10000); // Poll every 10s
    return () => clearInterval(interval);
  }, [fetchStatus]);

  const enqueueCommand = async (commandType, extraData = {}) => {
    if (!selectedEntryIds.length && commandType !== "open_url") return;
    setEnqueueing(commandType);
    try {
      const payload = {
        command_type: commandType,
        entry_ids: selectedEntryIds,
        ...extraData,
      };
      const res = await api.post("/bridge/enqueue", payload);
      fetchStatus();
      return res.data;
    } catch (err) {
      console.error("Enqueue failed:", err);
    } finally {
      setEnqueueing(null);
    }
  };

  const clearPending = async () => {
    try {
      await api.delete("/bridge/commands/clear");
      fetchStatus();
    } catch (err) {
      console.error("Clear failed:", err);
    }
  };

  const handleDetectAccounts = async () => {
    setEnqueueing("detect_accounts");
    try {
      await api.post("/bridge/enqueue", { command_type: "detect_accounts" });
      fetchStatus();
    } catch (err) {
      console.error("Detect failed:", err);
    } finally {
      setEnqueueing(null);
    }
  };

  const handleSyncProfiles = async () => {
    setEnqueueing("sync_profiles");
    try {
      const res = await api.post("/bridge/sync-profiles");
      alert(`Synced ${res.data.synced} profiles from AdsPower (total: ${res.data.total_profiles})`);
      fetchStatus();
    } catch (err) {
      alert(err.response?.data?.detail || "Failed to sync profiles. Check AdsPower API key in Settings.");
    } finally {
      setEnqueueing(null);
    }
  };

  const handleSaveMapping = async (profileId) => {
    if (!mappingInput.trim()) { setEditMapping(null); return; }
    try {
      const existing = mappings.find(m => m.profile_id === profileId);
      await api.put("/bridge/mappings", {
        profile_id: profileId,
        profile_name: existing?.profile_name || "",
        reddit_account: mappingInput.trim(),
      });
      fetchStatus();
    } catch (err) {
      console.error("Save mapping failed:", err);
    }
    setEditMapping(null);
  };

  const handleDeleteMapping = async (profileId) => {
    try {
      await api.delete(`/bridge/mappings/${profileId}`);
      fetchStatus();
    } catch (err) {
      console.error("Delete mapping failed:", err);
    }
  };

  const isOnline = bridgeStatus?.status === "online";
  const profiles = bridgeStatus?.profiles || [];

  const serverUrl = process.env.REACT_APP_BACKEND_URL || window.location.origin;

  if (loading) {
    return (
      <div style={{ padding: 30, textAlign: "center", color: "#6b7280" }}>
        <Loader2 size={20} className="animate-spin" style={{ margin: "0 auto 8px", display: "block" }} />
        Loading bridge status...
      </div>
    );
  }

  return (
    <div style={{ padding: "16px 0" }} data-testid="bridge-control">
      {/* Connection Status */}
      <div style={{
        background: "#18181b", border: "1px solid #27272a", borderRadius: 10, padding: 16, marginBottom: 16,
      }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            {isOnline ? <Wifi size={16} color="#10b981" /> : <WifiOff size={16} color="#ef4444" />}
            <span style={{ fontSize: 14, fontWeight: 600, color: isOnline ? "#10b981" : "#ef4444" }}>
              Bridge {isOnline ? "Online" : "Offline"}
            </span>
            {isOnline && profiles.length > 0 && (
              <span style={{ fontSize: 11, color: "#6b7280", background: "#27272a", padding: "2px 8px", borderRadius: 10 }}>
                {profiles.length} profile{profiles.length !== 1 ? "s" : ""}
              </span>
            )}
          </div>
          <button onClick={fetchStatus} style={{ background: "none", border: "none", color: "#6b7280", cursor: "pointer" }}>
            <RotateCcw size={14} />
          </button>
        </div>

        {!isOnline && (
          <div style={{ background: "#111113", borderRadius: 8, padding: 14, fontSize: 12, color: "#9ca3af", lineHeight: 1.6 }}>
            <div style={{ marginBottom: 8, color: "#e5e5e5", fontWeight: 500 }}>Setup Instructions:</div>
            <div style={{ marginBottom: 8 }}>Run this on your machine (where AdsPower is installed):</div>
            <div style={{
              background: "#0a0a0a", padding: 10, borderRadius: 6, fontFamily: "JetBrains Mono, monospace",
              fontSize: 11, color: "#60a5fa", overflowX: "auto", whiteSpace: "pre", marginBottom: 8,
            }}>
              {`pip install requests selenium\npython bridge.py --server ${serverUrl} --api-key YOUR_ADSPOWER_API_KEY`}
            </div>
            <a
              href={`${serverUrl}/api/bridge/download`}
              download="bridge.py"
              style={{ color: "#f97316", fontSize: 12, display: "inline-flex", alignItems: "center", gap: 4 }}
              data-testid="download-bridge"
            >
              <Download size={12} /> Download bridge.py
            </a>
          </div>
        )}

        {isOnline && profiles.length > 0 && (
          <div style={{ marginTop: 8 }}>
            <div style={{ fontSize: 11, color: "#6b7280", marginBottom: 6 }}>Connected Profiles:</div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
              {profiles.slice(0, 8).map(p => (
                <span key={p.id} style={{
                  fontSize: 11, background: "#27272a", color: "#e5e5e5", padding: "3px 8px",
                  borderRadius: 6, display: "inline-flex", alignItems: "center", gap: 4,
                }}>
                  <Monitor size={10} color="#60a5fa" />
                  {p.name || `#${p.serial_number}`}
                </span>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Quick Actions */}
      {isOnline && (
        <div style={{
          background: "#18181b", border: "1px solid #27272a", borderRadius: 10, padding: 16, marginBottom: 16,
        }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: "#e5e5e5", marginBottom: 12 }}>
            Quick Actions
            {selectedEntryIds.length > 0 && (
              <span style={{ fontSize: 11, color: "#f97316", marginLeft: 8 }}>
                ({selectedEntryIds.length} selected)
              </span>
            )}
          </div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
            <button
              className="btn btn-secondary"
              onClick={() => enqueueCommand("fetch_views")}
              disabled={!selectedEntryIds.length || enqueueing === "fetch_views"}
              data-testid="bridge-fetch-views"
              style={{ fontSize: 12 }}
            >
              {enqueueing === "fetch_views" ? <Loader2 size={13} className="animate-spin" /> : <Eye size={13} />}
              {" Fetch Views"}
            </button>
            <button
              className="btn btn-secondary"
              onClick={() => enqueueCommand("fetch_stats")}
              disabled={!selectedEntryIds.length || enqueueing === "fetch_stats"}
              data-testid="bridge-fetch-stats"
              style={{ fontSize: 12 }}
            >
              {enqueueing === "fetch_stats" ? <Loader2 size={13} className="animate-spin" /> : <Play size={13} />}
              {" Fetch All Stats"}
            </button>
            <button
              className="btn btn-secondary"
              onClick={handleDetectAccounts}
              disabled={enqueueing === "detect_accounts"}
              data-testid="bridge-detect-accounts"
              style={{ fontSize: 12, borderColor: "#f9731655", color: "#f97316" }}
            >
              {enqueueing === "detect_accounts" ? <Loader2 size={13} className="animate-spin" /> : <Monitor size={13} />}
              {" Detect Reddit Accounts"}
            </button>
            <button
              className="btn btn-secondary"
              onClick={handleSyncProfiles}
              disabled={enqueueing === "sync_profiles"}
              data-testid="bridge-sync-profiles"
              style={{ fontSize: 12, borderColor: "#3b82f655", color: "#3b82f6" }}
            >
              {enqueueing === "sync_profiles" ? <Loader2 size={13} className="animate-spin" /> : <RotateCcw size={13} />}
              {" Sync All AdsPower Profiles"}
            </button>
          </div>
          {!selectedEntryIds.length && (
            <div style={{ fontSize: 11, color: "#6b7280", marginTop: 8 }}>
              Select entries in the Posts & Comments table for Fetch actions.
            </div>
          )}
        </div>
      )}

      {/* Profile Mappings */}
      <div style={{
        background: "#18181b", border: "1px solid #27272a", borderRadius: 10, padding: 16, marginBottom: 16,
      }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12 }}>
          <span style={{ fontSize: 13, fontWeight: 600, color: "#e5e5e5" }}>
            Profile → Reddit Account Mappings
          </span>
          <span style={{ fontSize: 11, color: "#6b7280" }}>
            {mappings.length} mapped
          </span>
        </div>

        {mappings.length === 0 ? (
          <div style={{ fontSize: 12, color: "#4b5563", padding: 16, textAlign: "center", lineHeight: 1.6 }}>
            No profile mappings yet.
            {isOnline ? (
              <> Click <strong style={{ color: "#f97316" }}>Detect Reddit Accounts</strong> above to auto-detect.</>
            ) : (
              <> Start the bridge, then click "Detect Reddit Accounts".</>
            )}
          </div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            {mappings.map(m => (
              <div key={m.profile_id} style={{
                display: "flex", alignItems: "center", gap: 8, padding: "8px 10px",
                background: "#111113", borderRadius: 6, fontSize: 12,
              }}>
                <Monitor size={13} color="#60a5fa" style={{ flexShrink: 0 }} />
                <span style={{ color: "#9ca3af", minWidth: 90, fontFamily: "JetBrains Mono", fontSize: 11 }}>
                  {m.profile_name || m.profile_id.substring(0, 8)}
                </span>
                <span style={{ color: "#4b5563" }}>→</span>

                {editMapping === m.profile_id ? (
                  <input
                    autoFocus
                    value={mappingInput}
                    onChange={e => setMappingInput(e.target.value)}
                    onBlur={() => handleSaveMapping(m.profile_id)}
                    onKeyDown={e => { if (e.key === 'Enter') handleSaveMapping(m.profile_id); if (e.key === 'Escape') setEditMapping(null); }}
                    placeholder="u/username"
                    style={{ flex: 1, padding: "2px 6px", fontSize: 12, background: "#27272a", border: "1px solid #3b82f6", borderRadius: 4, color: "#e5e5e5", fontFamily: "JetBrains Mono", outline: "none" }}
                    data-testid={`mapping-input-${m.profile_id}`}
                  />
                ) : (
                  <span
                    onClick={() => { setEditMapping(m.profile_id); setMappingInput(m.reddit_account || ""); }}
                    style={{ color: "#e5e5e5", cursor: "pointer", flex: 1, fontFamily: "JetBrains Mono" }}
                    title="Click to edit"
                    data-testid={`mapping-account-${m.profile_id}`}
                  >
                    {m.reddit_account || "—"}
                  </span>
                )}

                <span style={{
                  fontSize: 9, padding: "1px 5px", borderRadius: 4,
                  background: m.source === "auto_detect" ? "#1e3a5f" : "#2d1f0e",
                  color: m.source === "auto_detect" ? "#60a5fa" : "#f97316",
                }}>
                  {m.source === "auto_detect" ? "auto" : "manual"}
                </span>

                <button
                  onClick={() => handleDeleteMapping(m.profile_id)}
                  style={{ background: "none", border: "none", color: "#4b5563", cursor: "pointer", padding: 2 }}
                  data-testid={`delete-mapping-${m.profile_id}`}
                >
                  <Trash2 size={12} />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Activity Log */}
      <div style={{
        background: "#18181b", border: "1px solid #27272a", borderRadius: 10, padding: 16,
      }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12 }}>
          <span style={{ fontSize: 13, fontWeight: 600, color: "#e5e5e5" }}>Activity Log</span>
          {history.some(h => h.status === "pending" || h.status === "in_progress") && (
            <button onClick={clearPending} style={{
              background: "none", border: "1px solid #374151", borderRadius: 6,
              padding: "3px 8px", fontSize: 11, color: "#6b7280", cursor: "pointer",
              display: "flex", alignItems: "center", gap: 4,
            }} data-testid="clear-pending">
              <Trash2 size={10} /> Clear Pending
            </button>
          )}
        </div>

        {history.length === 0 ? (
          <div style={{ fontSize: 12, color: "#4b5563", padding: 20, textAlign: "center" }}>
            No activity yet. Select entries and use Quick Actions to get started.
          </div>
        ) : (
          <div style={{ maxHeight: 300, overflowY: "auto" }}>
            {history.map(cmd => (
              <div key={cmd.id} style={{
                display: "flex", alignItems: "center", gap: 8, padding: "8px 0",
                borderBottom: "1px solid #1f1f23", fontSize: 12,
              }}>
                {cmd.status === "completed" && <CheckCircle size={13} color="#10b981" />}
                {cmd.status === "failed" && <XCircle size={13} color="#ef4444" />}
                {cmd.status === "pending" && <Clock size={13} color="#f59e0b" />}
                {cmd.status === "in_progress" && <Loader2 size={13} color="#3b82f6" className="animate-spin" />}

                <span style={{ color: "#e5e5e5", fontWeight: 500, minWidth: 80 }}>
                  {cmd.command_type === "fetch_views" ? "Fetch Views" :
                   cmd.command_type === "fetch_stats" ? "Fetch Stats" :
                   cmd.command_type === "post_comment" ? "Post Comment" :
                   cmd.command_type}
                </span>

                <span style={{ color: "#6b7280", flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {cmd.url ? cmd.url.substring(0, 50) + (cmd.url.length > 50 ? "..." : "") : "-"}
                </span>

                <span style={{ color: STATUS_COLORS[cmd.status] || "#6b7280", fontSize: 11, flexShrink: 0 }}>
                  {cmd.status}
                </span>

                {cmd.result?.views != null && (
                  <span style={{ fontFamily: "JetBrains Mono", fontSize: 11, color: "#60a5fa", flexShrink: 0 }}>
                    {cmd.result.views} views
                  </span>
                )}

                {cmd.error && (
                  <span style={{ fontSize: 10, color: "#ef4444", maxWidth: 150, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={cmd.error}>
                    {cmd.error}
                  </span>
                )}

                <span style={{ fontSize: 10, color: "#4b5563", flexShrink: 0 }}>
                  {cmd.created_at ? new Date(cmd.created_at).toLocaleTimeString() : ""}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
